import { Injectable, NgZone } from '@angular/core';
import {
  AsyncSubject,
  BehaviorSubject,
  Observable,
  Observer,
  throwError
} from 'rxjs';

import * as Raven from 'raven-js';
import { environment } from '../../environments/environment';

declare var window: any;
declare var FB: any;

export enum SharePlatform {
  Facebook = 'facebook',
  Twitter = 'twitter',
  LinkedIn = 'linkedin',
  Email = 'email',
  CopyLink = 'copylink',
}

@Injectable()
export class SharingService {

  private baseUrl: string;
  private openParams: string = 'height=420px,width=550px,status=1';

  constructor(
    private zone: NgZone,
  ) {

    // set the base url
    this.baseUrl = `https://${window.location.host}/${window.location.pathname.split('/')[1]}/job-details/`;
  }

  /**
   * Shares a given job to a particular platform
   * @param { SharePlatform } platform - the platform we want to share it on
   * @param { object } job - The job object which we want to share
   */
  share(platform: SharePlatform, job: object) {
    switch (platform) {
      case SharePlatform.Facebook:
        window.open(`https://www.facebook.com/sharer.php?u=${encodeURIComponent(this.baseUrl + job['id'])}`, 'facebook share', this.openParams);
        break;
      case SharePlatform.Twitter:
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(this.baseUrl + job['id'])}&text=${encodeURIComponent(this.getJobSummary(job))}`, 'twitter share', this.openParams);
        break;
      case SharePlatform.LinkedIn:
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${this.baseUrl}${job['id']}&title=${job['jobTitle']}&summary=${encodeURIComponent(this.getJobSummary(job))}`,
                    'linkedin share', this.openParams);
        break;
      case SharePlatform.Email:
        window.location.href = `mailto:?body=${this.baseUrl}${job['id']}&subject=${job['jobTitle']}`;
        break;
      case SharePlatform.CopyLink:
        this.shareCopyLink(job);
        break;
    }
  }

  /**
   * Private function that will copy the link to the users clipboard
   * @param { object } job - the job to be shared
   */
  private shareCopyLink(job: object) {
    // see https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript

    // create texarea and move it off screen
    const textarea = document.createElement('textarea');
    textarea.style.cssText = 'position: absolute; left: -99999em';

    // Set to readonly to prevent mobile devices opening a keyboard when
    // text is .select()'ed.
    textarea.setAttribute('readonly', 'true');

    document.body.appendChild(textarea);

    textarea.value = `${this.baseUrl}${job['id']}`;

    // Check if there is any content selected previously.
    const selected = document.getSelection().rangeCount > 0 ?
      document.getSelection().getRangeAt(0) : false;

    // iOS Safari blocks programmtic execCommand copying normally, without this hack.
    // https://stackoverflow.com/questions/34045777/copy-to-clipboard-using-javascript-in-ios
    if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
      const editable = textarea.contentEditable;
      textarea.contentEditable = 'true';
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      textarea.setSelectionRange(0, 999999);
      textarea.contentEditable = editable;
    } else {
      textarea.select();
    }

    try {
      const result = document.execCommand('copy');

      // Restore previous selection.
      if (selected) {
        document.getSelection().removeAllRanges();
        document.getSelection().addRange(selected);
      }
    } catch (err) {
      this.handleError(err);
    }
  }

  /**
   * Returns a job summary
   *
   * @param { object }
   * @return { string }
   */
  private getJobSummary(job: object): string {
    const name = (job['brandTemplateName']) ? job['brandTemplateName'] : job['company'];
    return `Apply for a ${job['jobTitle']} Job in ${job['city']}, ${job['stateProvCode']}.`;
  }

  /**
   * Handles the share service errors
   *
   * @param { any }
   * @return { Observable }
   */
  private handleError(error: any): Observable<any> {
    Raven.captureException(error);
    return throwError(error);
  }

}
